import { Controller } from "@hotwired/stimulus"

// Single modal that displays one of several pre-rendered answer panels.
// open(event) reads the requested topic from event.params.topic
// (data-help-topic-modal-topic-param="..."), reveals the matching panel, then
// shows the overlay. Mirrors bundle_modal_controller.js (scroll lock, backdrop
// click, Escape). All answer panels live inside the modal markup.
//
// TWO THINGS THE DIALOG OWES ITS USER, both of which were missing:
//
// · A NAME. `role="dialog" aria-modal="true"` with neither aria-label nor
//   aria-labelledby announces as an unlabelled dialog. The heading that names it
//   belongs to whichever panel is showing, so showPanel() points
//   aria-labelledby at that panel's h3 and close() removes the attribute again —
//   leaving it pointed at a hidden heading would resolve to an empty name, which
//   is worse than the static aria-label fallback in the markup.
//
// · FOCUS BACK. open() moves focus into the dialog. On close the browser puts
//   focus on <body>, so a keyboard user is returned to the top of the document
//   and has to tab all the way down to the topic list again. The opener is
//   captured on the way in and refocused on the way out.
export default class extends Controller {
  static targets = ["modal", "backdrop", "panel", "dialog"]
  static values = { open: Boolean }

  connect() {
    this.handleEscape = this.handleEscape.bind(this)
    document.addEventListener("keydown", this.handleEscape)
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleEscape)
    if (this.openValue) this.restoreBodyScroll()
    this.opener = null
  }

  open(event) {
    const topic = event && event.params ? event.params.topic : null
    // currentTarget, not activeElement: a click leaves focus on the button in
    // every engine we care about, but a keyboard Enter on a <button> fires the
    // click with the button focused too, and currentTarget is the element that
    // carries the action either way.
    this.opener = (event && event.currentTarget) || document.activeElement
    this.showPanel(topic)
    if (!this.hasModalTarget) return
    this.modalTarget.classList.remove("hidden")
    this.modalTarget.classList.add("flex")
    this.preventBodyScroll()
    this.openValue = true
    this.focusClose()
  }

  close() {
    if (!this.hasModalTarget) return
    this.modalTarget.classList.add("hidden")
    this.modalTarget.classList.remove("flex")
    this.restoreBodyScroll()
    this.openValue = false
    // Drop the label before the panel goes away with it.
    if (this.hasDialogTarget) this.dialogTarget.removeAttribute("aria-labelledby")
    this.restoreFocus()
  }

  // Reveal the panel whose data-topic matches; hide the rest, and name the
  // dialog after the one that is now visible.
  showPanel(topic) {
    let heading = null
    this.panelTargets.forEach((panel) => {
      const shown = panel.dataset.topic === topic
      panel.classList.toggle("hidden", !shown)
      if (shown) heading = panel.querySelector("h3[id]")
    })
    if (!this.hasDialogTarget) return
    if (heading) {
      this.dialogTarget.setAttribute("aria-labelledby", heading.id)
    } else {
      this.dialogTarget.removeAttribute("aria-labelledby")
    }
  }

  handleBackdropClick(event) {
    if (this.hasBackdropTarget && event.target === this.backdropTarget) this.close()
  }

  handleEscape(event) {
    if (event.key === "Escape" && this.openValue) this.close()
  }

  preventBodyScroll() {
    document.body.style.overflow = "hidden"
  }

  restoreBodyScroll() {
    document.body.style.overflow = ""
  }

  focusClose() {
    setTimeout(() => {
      const el = this.modalTarget.querySelector("button")
      if (el) el.focus()
    }, 50)
  }

  restoreFocus() {
    const el = this.opener
    this.opener = null
    // isConnected: a Turbo render between open and close can replace the button.
    if (el && el.isConnected && typeof el.focus === "function") el.focus()
  }
}
